import { useState } from 'react';
import styled from 'styled-components';
import { ClientBankAccount } from 'shared-components';
import { Button, Input } from 'shared-components';
import LoadingOverlay from 'Abstracts/Components/LoadingOverlay';
import { Colors } from 'Abstracts';

export type EditableClientBankAccount = Pick<
	ClientBankAccount,
	'ClientBankAccountName' | 'ClientBankAccountBsb' | 'ClientBankAccountAccount'
>;

interface EditBankAccountFormProps {
	bankAccount: EditableClientBankAccount;
	setBankAccount: (account: EditableClientBankAccount) => void;
	onSave: (account: EditableClientBankAccount) => Promise<void>;
	saveButtonText: string;
}

export const formatBsb = (bsb: string) => {
	// remove any non-numeric characters
	const val = ('' + bsb).replace(/[^0-9]/g, '');
	// put a dash in the middle
	if (val.length === 6) {
		const newVal = val.slice(0, 3) + '-' + val.slice(3);
		return newVal;
	}
	return val;
};
const isBsbValid = (bsb: string | null) => {
	if (bsb === null) return false;
	return ('' + bsb).match(/^[0-9]{3}-[0-9]{3}$/);
};
const isAccountNumberValid = (accountNumber: string | null) => {
	if (accountNumber === null) return false;
	const val = '' + accountNumber;
	return /^[0-9]{6,9}$/.test(val);
};

export default function BankAccountForm(props: EditBankAccountFormProps) {
	const { bankAccount, setBankAccount, onSave, saveButtonText } = props;
	const [loading, setLoading] = useState(false);

	const [validateActive, setValidateActive] = useState(false);

	const isValid =
		!validateActive ||
		(bankAccount.ClientBankAccountName &&
			bankAccount.ClientBankAccountName.length > 0 &&
			isBsbValid(bankAccount.ClientBankAccountBsb) &&
			isAccountNumberValid(bankAccount.ClientBankAccountAccount));

	return (
		<Container>
			{loading && <LoadingOverlay text="Saving bank account details" />}
			<div className="input-container">
				<Input
					className="account-name"
					label="Account name"
					placeholder="Account name"
					value={bankAccount.ClientBankAccountName}
					onChange={(val) => setBankAccount({ ...bankAccount, ClientBankAccountName: val })}
				/>
				{validateActive && bankAccount.ClientBankAccountName.length === 0 && (
					<div className="validation-container">An account name is required</div>
				)}
			</div>
			<div className="input-container">
				<Input
					className="account-bsb"
					label="BSB"
					placeholder="BSB"
					value={bankAccount.ClientBankAccountBsb}
					onBlur={(e) => setBankAccount({ ...bankAccount, ClientBankAccountBsb: formatBsb(e.target.value) })}
					onChange={(val) => setBankAccount({ ...bankAccount, ClientBankAccountBsb: val })}
				/>
				{validateActive && !isBsbValid(bankAccount.ClientBankAccountBsb) && (
					<div className="validation-container">Invalid BSB number</div>
				)}
			</div>
			<div className="input-container">
				<Input
					className="account-number"
					label="Account number"
					placeholder="Account number"
					value={bankAccount.ClientBankAccountAccount}
					onBlur={(e) => {
						// remove any non-numeric characters
						const val = e.target.value.replace(/[^0-9]/g, '');
						setBankAccount({ ...bankAccount, ClientBankAccountAccount: val });
					}}
					onChange={(val) => {
						const sanitized = val.replace(/[^0-9]/g, '');
						setBankAccount({ ...bankAccount, ClientBankAccountAccount: sanitized });
					}}
				/>
				{validateActive && !isAccountNumberValid(bankAccount.ClientBankAccountAccount) && (
					<div className="validation-container">Account number must be 6-9 digits</div>
				)}
			</div>
			<Button
				onClick={async () => {
					if (
						bankAccount.ClientBankAccountName.length === 0 ||
						!isBsbValid(bankAccount.ClientBankAccountBsb) ||
						!isAccountNumberValid(bankAccount.ClientBankAccountAccount)
					) {
						setValidateActive(true);
						return;
					}
					setLoading(true);
					try {
						await onSave({
							...bankAccount,
							ClientBankAccountBsb: bankAccount.ClientBankAccountBsb.replace(/-/g, ''),
						});
					} catch (e) {
						console.log(e);
					} finally {
						setLoading(false);
					}
				}}
				className={`btn-block create-btn`}
				disabled={!isValid}
				size="md"
				color="primary"
			>
				{saveButtonText}
			</Button>
		</Container>
	);
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;

	.create-btn {
		margin-block: 12px;
	}

	.form-group {
		margin: 0;
	}

	.input-container {
		.validation-container {
			color: ${Colors.red};
			font-size: 12px;
		}
		margin-bottom: 15px;
	}

	@media (max-width: 768px) {
		.input-container {
			width: 100%;
		}
	}
`;
